import { useEffect } from "react";
import { useFocusTrap, useFocusReturn } from "@mantine/hooks";

interface UseModalFocusAndScrollLockOptions {
  /** Whether the modal is logically open (controls focus-return lifecycle) */
  isOpen: boolean;
  /** Whether to apply focus trapping (default: false) */
  shouldTrapFocus?: boolean;
  /** Whether to lock document scrolling (defaults to isOpen) */
  shouldLockScroll?: boolean;
}

/**
 * Hook for managing modal focus trapping and body scroll locking.
 *
 * When modal opens:
 * - Locks body scroll while preserving scroll position
 * - Optionally traps focus within modal
 *
 * When modal closes:
 * - Restores body scroll and position
 * - Returns focus to trigger element
 *
 * @param options - Configuration options
 * @returns focusTrapRef - Ref to apply to the modal container
 *
 * @example
 * ```tsx
 * function MyModal({ isOpen, onClose }) {
 *   const focusTrapRef = useModalFocusAndScrollLock({
 *     isOpen,
 *     shouldTrapFocus: true,
 *   });
 *
 *   return (
 *     isOpen && (
 *       <div ref={focusTrapRef} className="modal">
 *         <h2>Modal Title</h2>
 *         <button onClick={onClose}>Close</button>
 *       </div>
 *     )
 *   );
 * }
 * ```
 */
const useModalFocusAndScrollLock = ({
  isOpen,
  shouldTrapFocus = false,
  shouldLockScroll = isOpen,
}: UseModalFocusAndScrollLockOptions) => {
  // Focus trap management
  const focusTrapRef = useFocusTrap(shouldTrapFocus && isOpen);
  useFocusReturn({ opened: isOpen, shouldReturnFocus: !isOpen });

  // Body scroll lock management
  useEffect(() => {
    if (shouldLockScroll) {
      const scrollY = window.scrollY;
      const lockedPathname = window.location.pathname;
      document.documentElement.dataset.kurbScrollLocked = "true";
      document.body.dataset.kurbScrollLocked = "true";

      return () => {
        delete document.documentElement.dataset.kurbScrollLocked;
        delete document.body.dataset.kurbScrollLocked;
        // Only restore the captured position if we're still on the page
        // where it was captured. Globally-mounted modals (e.g. ItemSlideIn)
        // can close as a side effect of a route change that already
        // happened — restoring a stale offset from the previous page can
        // land far past the end of a shorter destination page.
        if (window.location.pathname === lockedPathname) {
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [shouldLockScroll]);

  return focusTrapRef;
};

export default useModalFocusAndScrollLock;
